// The emphasis-cut core both offset readers render with. A block's
// `text.slice(start, end)` is split into runs at emphasis boundaries so italic
// spans survive into the reader instead of flattening to plain text, and every
// run keeps its absolute `start` offset into the immutable document `text`,
// which the renderer emits as `data-start` — the attribute `./dom-offsets`
// reads to map a DOM Selection back to document offsets.
//
// Parameterised on bare `{start, end}` spans rather than either app's
// `DocumentModel`: lekterion's zod model and @bindery/shared's are two
// different artifact formats, and this algorithm needs neither's extra fields.
// Retells renders the runs as they come (`splitLinkedRuns`); lekterion also
// cuts at zero-width footnote and anchor markers, so it takes the two pieces
// (`emphasisCuts`, `isEmphasized`) and interleaves its markers itself.

/** A half-open `[start, end)` offset span into a document's `text`. */
export interface Span {
  start: number;
  end: number;
}

/** A contiguous text run within a block; `em` marks an emphasized slice. */
export interface TextRun {
  start: number;
  value: string;
  em: boolean;
}

/**
 * The ascending, de-duplicated cut offsets for the block `[start, end)`: its
 * own two edges, every emphasis edge that falls strictly inside it, and any
 * `extra` offsets. Emphasis spans that only touch the block partially still cut
 * it at the boundary that lands inside. `extra` is added as given — the caller
 * clamps it into `[start, end]`.
 */
export function emphasisCuts(
  start: number,
  end: number,
  emphasis: readonly Span[],
  extra: Iterable<number> = [],
): number[] {
  const bounds = new Set<number>([start, end]);
  for (const e of emphasis) {
    if (e.start > start && e.start < end) bounds.add(e.start);
    if (e.end > start && e.end < end) bounds.add(e.end);
  }
  for (const o of extra) bounds.add(o);
  return [...bounds].sort((a, b) => a - b);
}

/** Whether the slice `[from, to)` lies wholly inside one emphasis span. */
export function isEmphasized(
  emphasis: readonly Span[],
  from: number,
  to: number,
): boolean {
  return emphasis.some((e) => e.start <= from && e.end >= to);
}

/**
 * Split `text[start, end)` into runs cut at every emphasis edge that falls
 * inside the block.
 */
export function splitEmphasisRuns(
  text: string,
  start: number,
  end: number,
  emphasis: readonly Span[],
): TextRun[] {
  const cuts = emphasisCuts(start, end, emphasis);

  const runs: TextRun[] = [];
  for (let i = 0; i < cuts.length - 1; i++) {
    const from = cuts[i]!;
    const to = cuts[i + 1]!;
    if (to <= from) continue;
    const em = isEmphasized(emphasis, from, to);
    runs.push({ start: from, value: text.slice(from, to), em });
  }
  return runs;
}

/** A span that renders as one interactive phrase, such as a context link. */
export interface LinkSpan extends Span {
  id: string;
}

/** A `TextRun` that also names the link it falls inside, if any. */
export interface LinkedRun extends TextRun {
  link: string | null;
}

/**
 * `splitEmphasisRuns`, also cut at every link edge inside the block, so each
 * run lies wholly inside or wholly outside one link. Links are assumed not to
 * overlap; the flattener drops the later of two that would.
 */
export function splitLinkedRuns(
  text: string,
  start: number,
  end: number,
  emphasis: readonly Span[],
  links: readonly LinkSpan[],
): LinkedRun[] {
  const linkEdges = links
    .flatMap((l) => [l.start, l.end])
    .filter((o) => o > start && o < end);
  const cuts = emphasisCuts(start, end, emphasis, linkEdges);

  const runs: LinkedRun[] = [];
  for (let i = 0; i < cuts.length - 1; i++) {
    const from = cuts[i]!;
    const to = cuts[i + 1]!;
    if (to <= from) continue;
    runs.push({
      start: from,
      value: text.slice(from, to),
      em: isEmphasized(emphasis, from, to),
      link: links.find((l) => l.start <= from && l.end >= to)?.id ?? null,
    });
  }
  return runs;
}
